import { Children, useEffect, useState } from 'react';
import {
  Box,
  HStack,
  Heading,
  Icon,
  IconButton,
  Text,
  Spinner,
  Flex,
  ButtonGroup,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { motion } from 'framer-motion';

export default function Slider({
  title,
  icon,
  children,
  loading = false,
  centerContent = false,
  autoAdvanceMs = 8000,
  pauseOnHover = true,
}) {
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const slides = Children.toArray(children).filter(Boolean);
  const canSlide = slides.length > 1;
  const currentSlide = slides[index];
  const fadeTransition = { duration: 0.25, ease: 'easeInOut' };
  const shouldPause = pauseOnHover && isHovered;
  useEffect(() => {
    if (index >= slides.length) {
      setIndex(0);
    }
  }, [index, slides.length]);

  useEffect(() => {
    if (loading || !canSlide || shouldPause || !autoAdvanceMs) return undefined;

    const intervalId = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, autoAdvanceMs);

    return () => clearInterval(intervalId);
  }, [autoAdvanceMs, canSlide, loading, shouldPause, slides.length]);

  const handlePrev = () => {
    if (!canSlide) return;
    setIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = () => {
    if (!canSlide) return;
    setIndex((prev) => (prev + 1) % slides.length);
  };

  return (
    <Box
      h="100%"
      minH={0}
      display="flex"
      flexDirection="column"
      zIndex={50}
      onMouseEnter={pauseOnHover ? () => setIsHovered(true) : undefined}
      onMouseLeave={pauseOnHover ? () => setIsHovered(false) : undefined}
    >
      {title && (
      <HStack
        justify="space-between"
        align="center"
        borderBottom="1px solid"
        borderColor="gray.100"
        pt={4}
        pb={4}
      >
        <HStack spacing={2}>
          <Icon as={icon} boxSize={4} color="orange.500" />
          <Heading as="h3" fontSize="sm" textTransform="uppercase">
            {title}
          </Heading>
        </HStack>
        {slides.length > 0 && (
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.500" minW="42px" textAlign="right">
              {`${index + 1} of ${slides.length}`}
            </Text>
            <ButtonGroup variant="outline" size="xs" spacing={1}>
              <IconButton
                aria-label="Previous slide"
                icon={<Icon as={FiChevronLeft} boxSize={4} color="gray.700" />}
                onClick={handlePrev}
                isDisabled={!canSlide}
                colorScheme="gray"
              />
              <IconButton
                aria-label="Next slide"
                icon={<Icon as={FiChevronRight} boxSize={4} color="gray.700" />}
                onClick={handleNext}
                isDisabled={!canSlide}
                colorScheme="gray"
              />
            </ButtonGroup>
          </HStack>
        )}
      </HStack>
      )}

      <Box
        flex="1"
        minH={0}
        overflow="visible"
        position="static"
      >
        {loading && (
          <motion.div
            key={`${title}-loading`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={fadeTransition}
            style={{ height: '100%' }}
          >
            <Flex h="100%" py={4} align="center" justify="center">
              <Box>
                <Spinner size="sm" color="orange.500" />
              </Box>
            </Flex>
          </motion.div>
        )}

        {!loading && slides.length > 0 && (
          <motion.div
            key={`${title}-${index}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={fadeTransition}
            style={{ height: '100%' }}
          >
            <Flex h="100%" overflowY="visible" py={4} minH={0}>
              <Flex
                w="100%"
                h="100%"
                align={centerContent ? 'center' : 'stretch'}
                justify={centerContent ? 'center' : 'flex-start'}
              >
                {currentSlide}
              </Flex>
            </Flex>
          </motion.div>
        )}

        {!loading && slides.length === 0 && (
          <motion.div
            key={`${title}-empty`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={fadeTransition}
            style={{ height: '100%' }}
          >
            <Flex h="100%" px={6} py={6} align="flex-start">
              <Text fontSize="sm" color="gray.600">No content available.</Text>
            </Flex>
          </motion.div>
        )}
      </Box>
      {slides.length > 1 && (
      <HStack
        justify="center"
        spacing={1}
        pt={4}
        pb={3}
        borderTop="1px solid"
        borderColor="gray.100"
      >
        {slides.map((_, itemIndex) => {
          const isActive = index === itemIndex;
          return (
            <Box
              key={itemIndex}
              as="button"
              type="button"
              aria-label={`Go to slide ${itemIndex + 1}`}
              onClick={() => setIndex(itemIndex)}
              h="8px"
              w={isActive ? '18px' : '8px'}
              borderRadius="full"
              bg={isActive ? 'orange.400' : 'gray.200'}
              transition="all 0.2s ease-in-out"
              _hover={{ bg: isActive ? 'orange.500' : 'gray.400' }}
            />
          );
        })}
      </HStack>
      )}
    </Box>
  );
}
